import api from './api';

export const barService = {
  // Configuration
  getBarConfig: async () => {
    try {
      const response = await api.get('/api/bar/config');
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Get bar config error:', error);
      let errorMessage = 'Failed to load bar configuration';
      if (error.response?.data?.detail) {
        errorMessage = typeof error.response.data.detail === 'string' 
          ? error.response.data.detail 
          : 'Failed to load bar configuration';
      }
      return {
        success: false,
        error: errorMessage,
        data: null
      };
    }
  },

  setActiveEvent: async (eventId) => {
    try {
      const response = await api.put('/api/bar/config', {
        active_event_id: eventId
      });
      return {
        success: true,
        data: response.data,
        message: 'Active event updated successfully'
      };
    } catch (error) {
      console.error('Set active event error:', error);
      let errorMessage = 'Failed to set active event';
      if (error.response?.data?.detail) {
        errorMessage = typeof error.response.data.detail === 'string' 
          ? error.response.data.detail 
          : 'Failed to set active event';
      }
      return {
        success: false,
        error: errorMessage
      };
    }
  },

  // Drinks
  getDrinks: async () => {
    try {
      const response = await api.get('/api/bar/drinks');
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Get drinks error:', error);
      let errorMessage = 'Failed to load drinks';
      if (error.response?.data?.detail) {
        errorMessage = typeof error.response.data.detail === 'string' 
          ? error.response.data.detail 
          : 'Failed to load drinks';
      }
      return {
        success: false,
        error: errorMessage,
        data: []
      };
    }
  },

  createDrink: async (drinkData) => {
    try {
      const response = await api.post('/api/bar/drinks', drinkData);
      return {
        success: true,
        data: response.data,
        message: 'Drink created successfully'
      };
    } catch (error) {
      console.error('Create drink error:', error);
      let errorMessage = 'Failed to create drink';
      if (error.response?.data?.detail) {
        errorMessage = typeof error.response.data.detail === 'string' 
          ? error.response.data.detail 
          : 'Failed to create drink';
      }
      return {
        success: false,
        error: errorMessage
      };
    }
  },

  updateDrink: async (drinkId, drinkData) => {
    try {
      const response = await api.put(`/api/bar/drinks/${drinkId}`, drinkData);
      return {
        success: true,
        data: response.data,
        message: 'Drink updated successfully'
      };
    } catch (error) {
      console.error('Update drink error:', error);
      let errorMessage = 'Failed to update drink';
      if (error.response?.data?.detail) {
        errorMessage = typeof error.response.data.detail === 'string' 
          ? error.response.data.detail 
          : 'Failed to update drink';
      }
      return {
        success: false,
        error: errorMessage
      };
    }
  },

  deleteDrink: async (drinkId) => {
    try {
      await api.delete(`/api/bar/drinks/${drinkId}`);
      return {
        success: true,
        message: 'Drink deleted successfully'
      };
    } catch (error) {
      console.error('Delete drink error:', error);
      let errorMessage = 'Failed to delete drink';
      if (error.response?.data?.detail) {
        errorMessage = typeof error.response.data.detail === 'string' 
          ? error.response.data.detail 
          : 'Failed to delete drink';
      }
      return {
        success: false,
        error: errorMessage
      };
    }
  },

  // Transactions
  addTransaction: async (userId, drinkId, quantity = 1) => {
    try {
      const response = await api.post('/api/bar/transactions', {
        user_id: userId,
        drink_id: drinkId,
        quantity: quantity
      });
      return {
        success: true,
        data: response.data,
        message: 'Transaction added successfully'
      };
    } catch (error) {
      console.error('Add transaction error:', error);
      let errorMessage = 'Failed to add transaction';
      if (error.response?.data?.detail) {
        errorMessage = typeof error.response.data.detail === 'string' 
          ? error.response.data.detail 
          : 'Failed to add transaction';
      }
      return {
        success: false,
        error: errorMessage
      };
    }
  },

  getUserTransactions: async (userId, eventId = null) => {
    try {
      const url = eventId 
        ? `/api/bar/transactions/user/${userId}?event_id=${eventId}`
        : `/api/bar/transactions/user/${userId}`;
      const response = await api.get(url);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Get user transactions error:', error);
      let errorMessage = 'Failed to load transactions';
      if (error.response?.data?.detail) {
        errorMessage = typeof error.response.data.detail === 'string' 
          ? error.response.data.detail 
          : 'Failed to load transactions';
      }
      return {
        success: false,
        error: errorMessage,
        data: []
      };
    }
  },

  getEventTransactions: async (eventId) => {
    try {
      const response = await api.get(`/api/bar/transactions/event/${eventId}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Get event transactions error:', error);
      let errorMessage = 'Failed to load event transactions';
      if (error.response?.data?.detail) {
        errorMessage = typeof error.response.data.detail === 'string' 
          ? error.response.data.detail 
          : 'Failed to load event transactions';
      }
      return {
        success: false,
        error: errorMessage,
        data: []
      };
    }
  },

  getEventSummary: async (eventId) => {
    try {
      const response = await api.get(`/api/bar/transactions/summary/${eventId}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Get event summary error:', error);
      let errorMessage = 'Failed to load event summary';
      if (error.response?.data?.detail) {
        errorMessage = typeof error.response.data.detail === 'string' 
          ? error.response.data.detail 
          : 'Failed to load event summary';
      }
      return {
        success: false,
        error: errorMessage,
        data: null
      };
    }
  },

  // Participants
  getActiveEventParticipants: async () => {
    try {
      const response = await api.get('/api/bar/participants');
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Get participants error:', error);
      let errorMessage = 'Failed to load participants';
      if (error.response?.data?.detail) {
        errorMessage = typeof error.response.data.detail === 'string' 
          ? error.response.data.detail 
          : 'Failed to load participants';
      }
      return {
        success: false,
        error: errorMessage,
        data: []
      };
    }
  },

  // Delete transaction (undo)
  deleteTransaction: async (transactionId) => {
    try {
      await api.delete(`/api/bar/transactions/${transactionId}`);
      return {
        success: true,
        message: 'Transaction deleted successfully'
      };
    } catch (error) {
      console.error('Delete transaction error:', error);
      let errorMessage = 'Failed to delete transaction';
      if (error.response?.data?.detail) {
        errorMessage = typeof error.response.data.detail === 'string' 
          ? error.response.data.detail 
          : 'Failed to delete transaction';
      }
      return {
        success: false,
        error: errorMessage
      };
    }
  },

  // User Statistics
  getUserStatistics: async () => {
    try {
      const response = await api.get('/api/bar/stats');
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Get user statistics error:', error);
      let errorMessage = 'Failed to load statistics';
      if (error.response?.data?.detail) {
        errorMessage = typeof error.response.data.detail === 'string' 
          ? error.response.data.detail 
          : 'Failed to load statistics';
      }
      return {
        success: false,
        error: errorMessage,
        data: null
      };
    }
  },

  // Recent Orders
  getRecentOrders: async (limit = 3) => {
    try {
      const response = await api.get(`/api/bar/orders/recent?limit=${limit}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Get recent orders error:', error);
      let errorMessage = 'Failed to load recent orders';
      if (error.response?.data?.detail) {
        errorMessage = typeof error.response.data.detail === 'string' 
          ? error.response.data.detail 
          : 'Failed to load recent orders';
      }
      return {
        success: false,
        error: errorMessage,
        data: []
      };
    }
  },

  // Active Event Summary
  getActiveEventSummary: async () => {
    try {
      const response = await api.get('/api/bar/active-event/summary');
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Get active event summary error:', error);
      let errorMessage = 'Failed to load active event summary';
      if (error.response?.data?.detail) {
        errorMessage = typeof error.response.data.detail === 'string' 
          ? error.response.data.detail 
          : 'Failed to load active event summary';
      }
      return {
        success: false,
        error: errorMessage,
        data: null
      };
    }
  },

  // User self-service transaction
  addMyTransaction: async (drinkId, quantity = 1) => {
    try {
      const response = await api.post('/api/bar/transactions/me', {
        drink_id: drinkId,
        quantity: quantity
      });
      return {
        success: true,
        data: response.data,
        message: 'Order placed successfully'
      };
    } catch (error) {
      console.error('Add my transaction error:', error);
      let errorMessage = 'Failed to place order';
      if (error.response?.data?.detail) {
        errorMessage = typeof error.response.data.detail === 'string' 
          ? error.response.data.detail 
          : 'Failed to place order';
      }
      return {
        success: false,
        error: errorMessage
      };
    }
  },

  // Get current user's transactions
  getMyTransactions: async (eventId = null) => {
    try {
      const url = eventId 
        ? `/api/bar/transactions/me?event_id=${eventId}`
        : `/api/bar/transactions/me`;
      const response = await api.get(url);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Get my transactions error:', error);
      return {
        success: false,
        error: 'Failed to load your transactions',
        data: []
      };
    }
  },

  // Delete own transaction
  deleteMyTransaction: async (transactionId) => {
    try {
      await api.delete(`/api/bar/transactions/me/${transactionId}`);
      return {
        success: true,
        message: 'Order cancelled successfully'
      };
    } catch (error) {
      console.error('Delete my transaction error:', error);
      let errorMessage = 'Failed to cancel order';
      if (error.response?.data?.detail) {
        errorMessage = typeof error.response.data.detail === 'string' 
          ? error.response.data.detail 
          : 'Failed to cancel order';
      }
      return {
        success: false,
        error: errorMessage
      };
    }
  }
};

export default barService;

